import { Injectable, inject } from '@angular/core'
import { CurrencyPipe } from '@angular/common'

import { AuctionModel } from '../../modules/public/models/auction.model'
import {
    LotStatusUpdatePayload,
    HubClientPacket,
    LotCommencementPayload,
    LotPricingPayload,
    LotInformationPayload,
    LotAutoBidConfigurationPayload,
    SaleyardLotUpdatedPayload,
    SaleyardLotCreatedPayload,
    LotOrderUpdatePayload,
    LotStatus,
    LotSalePriceChangePayload,
    LotPostAuctionSaleCreatedPayload,
    OfferUpdatePayload,
    SaleyardLotMediaAddedPayload,
    SaleyardLotMediaRemovedPayload,
    AuctionCurrentLotCancelStartPayload
} from '../../core/models/hub-client/hub-client-packet.model'
import { LotActivityStatus, PricingOptions } from '../../core/constants'
import { LotModel } from '../../modules/public/models/lot.model'
import { MediaType } from '../../modules/public/models/media-type.enum'
import { MetaType } from '../../modules/public/models/meta-type.enum'
import { AppSpinnerService } from '../../core/services/app-spinner.service'
import { SoundService } from '../../core/services/sound.service'
import { ReserveStatus } from '../../core/constants/reserve-status.enum'
import { MessageService } from '../../core/services/message.service'
import { AuctionResponse } from '../../modules/manage/shared/models/auction-response.model'
import { LotType } from '../../core/constants/lot-type'
import { LotOfferStatus } from '../../core/constants/lot-offer-status.enum'
import { LotHelper } from '../../modules/manage/saleyard/helpers/LotHelper'
import { AuctionHelper } from '../../modules/manage/saleyard/helpers/AuctionHelper'
import { objectKeysFirstLetterToLowerCase } from '../utils/object-helper'
import { Lot } from 'app/modules/manage/shared/models'
import { BuyerBidDiagnosticsService } from './buyer-bid-diagnostics.service'

@Injectable()
export class LotService {
    private currency = inject(CurrencyPipe)
    private spinner = inject(AppSpinnerService)
    private soundService = inject(SoundService)
    private buyerBidDiagnostics = inject(BuyerBidDiagnosticsService)
    readonly messageService = inject(MessageService)

    static message = {
        status: {
            startingSoon: 'Starting soon',
            nowSelling: 'Now selling',
            finishing: 'Finishing',
            paused: 'Paused'
        },
        result: {
            notSold: 'Lot not sold',
            passedIn: 'Lot passed in',
            sold: 'Lot sold',
            soldToUser: (soldPrice, unit) => `Lot sold to you for ${soldPrice}/${unit}`
        },
        validation: {
            invalid: 'Invalid number',
            twoDpAllowed: 'Only 2 decimal points allowed',
            lowNumber: 'Bid must be higher than current price + increment'
        }
    }

    timer: any
    timerUpdatedAt: any
    currentLot: any
    lastBidder: number | null = null

    setupLot(model: AuctionModel, r: any) {
        const lot = r.inProgressLot
        switch (lot.activityStatus) {
            case LotActivityStatus.ResumeDelayed:
            // Same as start delayed
            case LotActivityStatus.Started: {
                // ====== Setup started lot
                this.setupStartedLot(model, lot, lot.remainingTime)
                break
            }
            case LotActivityStatus.NowSelling: {
                // ====== Setup now selling lot
                this.setupNowSellingLot(model, lot.remainingTime, null, lot)
                break
            }
            case LotActivityStatus.Sold:
            case LotActivityStatus.SoldPostAuction:
            case LotActivityStatus.NotSold:
            case LotActivityStatus.PassedIn:
            case LotActivityStatus.WithdrawnNotSold:
            case LotActivityStatus.UnderOffer:
            case LotActivityStatus.OfferManagement: {
                this.setupFinishingLot(model, lot.activityStatus, lot, false)
                break
            }
            case LotActivityStatus.FinishDelayed: {
                // ====== Setup finishing lot
                this.setupFinishingLot(model, LotActivityStatus.FinishDelayed, lot, false)
                this.scrollFinishedLots()
                break
            }
            case LotActivityStatus.StartDelayPaused:
            case LotActivityStatus.Paused: {
                // ====== Setup paused lot
                this.setupPausedLot(model, lot)
                break
            }
        }

        // Setup media for a lot
        this.setupMedia(model.currentLot)
    }

    setupBid(model: AuctionModel, signal: HubClientPacket) {
        const payload = <LotStatusUpdatePayload>signal.Payload
        // Set current lot details from payload
        this.updateCurrentLot(model, payload)
        // Set user owns last bid state
        model.currentLot!.isUsersBid =
            model.auction!.bidderAccount != null &&
            model.currentLot!.winningBidderId === model.auction!.bidderAccount.id
        // Has user bid
        model.currentLot!.hasUserBid =
            model.currentLot!.biddersIds &&
            model.auction!.bidderAccount !== undefined &&
            model.currentLot!.biddersIds.includes(model.auction!.bidderAccount!.id)

        this.buyerBidDiagnostics.recordBidUpdate(
            model.currentLot,
            payload.Status,
            signal,
            model.auction?.bidderAccount?.id,
            this.lastBidder,
            model.auction?.id ?? null
        )

        // Check whether manual bid allowed
        if (model.currentLot!.allowManualBid) {
            // Validate manual bid
            const validation = this.validateBidAmount(
                model.currentLot!.currentPrice,
                model.currentLot!.increment!,
                model.currentLot!.manualBid!
            )
            // Setup validation result
            model.currentLot!.allowManualBid = validation.status
        }

        // Check whether auto bid is set and current price is greater than autobid
        if (model.currentLot!.autoBidIsSet && model.currentLot!.currentPrice! >= model.currentLot!.autoBid!) {
            // Remove auto bid
            model.currentLot!.autoBidIsSet = false
            // Reset autobid value
            model.currentLot!.autoBid = undefined
        }

        // Check whether auto bid is allowed
        if (model.currentLot!.allowAutoBid) {
            // Validate auto bid
            const validation = this.validateBidAmount(
                model.currentLot!.currentPrice,
                model.currentLot!.increment!,
                model.currentLot!.autoBid!
            )
            // Setup validation result
            model.currentLot!.allowAutoBid = validation.status
        }

        // Setup timer to wait for start lot / stop auction cmd
        this.setupTimer(model, payload.TimeRemaining)

        // Allow bidding
        model.canBid = true
        // Hide spinner
        this.spinner.hide()
    }

    setupLotPricingChange(model: AuctionModel, r: HubClientPacket) {
        // --- [Signal] Update lot increment
        const payload = <LotPricingPayload>r.Payload
        // Check whether increment change is not for current lot
        if (model.currentLot!.id !== payload.LotId) {
            return
        }

        // ++++++ Set lot increment
        const sound = this.messageService.getMessage('auction.sounds.lotDetailChange')
        // Update increment
        model.currentLot!.increment = payload.Increment
        // Calculate bid
        const bid = model.currentLot!.currentPrice! + model.currentLot!.increment
        // Setup bid
        model.currentLot!.bid = bid

        // Check whether manual bid allowed
        if (model.currentLot!.allowManualBid) {
            // Validate manual bid
            const validation = this.validateBidAmount(
                model.currentLot!.currentPrice,
                model.currentLot!.increment,
                model.currentLot!.manualBid!
            )
            // Setup validation result
            model.currentLot!.allowManualBid = validation.status
        }

        // Check whether autobid allowed
        if (model.currentLot!.autoBid) {
            // Validate auto bid
            const validation = this.validateBidAmount(
                model.currentLot!.currentPrice,
                model.currentLot!.increment,
                model.currentLot!.autoBid
            )
            // Setup validation result
            model.currentLot!.allowAutoBid = validation.status
        }
        if (sound !== undefined) {
            this.soundService.playSound(sound)
        }
    }

    setupStartedLot(model: AuctionModel, lot: LotModel | null, time, signal: HubClientPacket | null = null) {
        // Do not allow bidding
        model.canBid = false
        this.restoreCancelledLot(model, lot?.id)
        // Setup current lot
        this.setupCurrentLot(
            model,
            LotActivityStatus.Started,
            LotService.message.status.startingSoon,
            time,
            lot,
            signal
        )
        // Check whether reserve details are not provided
        if (model.currentLot!.reserveStatus == null) {
            // Init reserve status
            model.currentLot!.reserveStatus = 0
        }

        // Make sure that current lot is not listed in next up lots
        model.nextLots = model.nextLots.filter((i) => i.id !== model.currentLot!.id)
    }

    setupCancelCurrentLot(model: AuctionModel, payload: AuctionCurrentLotCancelStartPayload) {
        if (model.currentLot && model.currentLot.id !== payload.LotId) {
            return
        }

        const cancelledLot = model.currentLot

        if (!cancelledLot) {
            return
        }

        cancelledLot.activityStatus = LotActivityStatus.Active
        cancelledLot.lotClosing = false
        cancelledLot.nowSelling = false
        cancelledLot.statusMessage = undefined

        model.cancelledLot = cancelledLot
        model.canBid = false
    }

    private restoreCancelledLot(model: AuctionModel, nextCurrentLotId: number | null | undefined) {
        const cancelledLot = model.cancelledLot

        if (!cancelledLot) {
            return
        }

        model.cancelledLot = null

        if (cancelledLot.id === nextCurrentLotId) {
            return
        }

        model.nextLots = model.nextLots.filter((i) => i.id !== cancelledLot.id)
        model.nextLots = [cancelledLot, ...model.nextLots]
        this.sortLots(model.nextLots)
    }

    setupNowSellingLot(model: AuctionModel, time, signal: HubClientPacket | null = null, lot: LotModel | null = null) {
        this.restoreCancelledLot(model, lot?.id)
        // Setup current lot
        this.setupCurrentLot(
            model,
            LotActivityStatus.NowSelling,
            LotService.message.status.nowSelling,
            time,
            lot,
            signal
        )
        // Setup can bid flag
        model.canBid = true
    }

    setupPausedLot(model: AuctionModel, lot: Lot | null = null) {
        if (lot != null && typeof lot.remainingTime !== 'undefined') {
            // Setup timer
            model.timer = lot.remainingTime
        }

        // Setup current lot
        this.setupCurrentLot(model, LotActivityStatus.Paused, LotService.message.status.paused, model.timer, lot)
        // Stop timer
        clearInterval(this.timer)
    }

    setupNotSoldLot(model: AuctionModel, payload: LotStatusUpdatePayload) {
        let isCurrent = AuctionHelper.isLotCurrentById(model, payload.LotId)
        let lotToUpdate: LotModel | null | undefined = isCurrent
            ? model.currentLot
            : AuctionHelper.getNextLot(model, payload.LotId)
        if (lotToUpdate) {
            // Setup lot result message
            lotToUpdate.resultMessage = LotService.message.result.notSold
            if (LotHelper.isTallyDefinedOnPayload(payload)) {
                lotToUpdate.tally = Number(payload.Tally || 0)
            }
            this.finalizeLot(model, payload, lotToUpdate, LotActivityStatus.NotSold, isCurrent)
        }
    }

    setupSoldLot(model: AuctionModel, payload: LotStatusUpdatePayload) {
        let isCurrent = AuctionHelper.isLotCurrentById(model, payload.LotId)
        let lotToUpdate: LotModel | null | undefined = isCurrent
            ? model.currentLot
            : AuctionHelper.getNextLot(model, payload.LotId)
        if (lotToUpdate) {
            let resultMsg = LotService.message.result.sold
            // Setup sold price
            lotToUpdate.soldPrice = payload.CurrentPrice
            // Setup purchaser name for Generic Auction
            lotToUpdate.purchaserName = payload.PurchaserName
            lotToUpdate.purchaserAccount = payload.PurchaserAccount

            if (payload.PricePerUnit) {
                lotToUpdate.pricePerKg = payload.PricePerUnit
            }

            if (LotHelper.isTallyDefinedOnPayload(payload)) {
                lotToUpdate.tally = Number(payload.Tally || 0)
            }

            // Setup winning lot state
            if (model.auction!.bidderAccount != null && payload.WinningBidderId === model.auction!.bidderAccount.id) {
                // Get sold price in currency format
                const soldPrice = this.currency.transform(lotToUpdate.soldPrice)
                // Update result message
                resultMsg = LotService.message.result.soldToUser(soldPrice, lotToUpdate.incrementUnits)
                // Set user won indicator
                lotToUpdate.userWonBidding = true
            }

            if (
                lotToUpdate.userWonBidding &&
                !LotHelper.isPick(lotToUpdate) && //not picks parent
                !(LotHelper.isRunout(lotToUpdate) && lotToUpdate.tally! < 1) // not runout with 0 tally
            ) {
                const sound = this.messageService.getMessage('auction.sounds.bidWin')
                this.soundService.playSound(sound)
            }

            // Setup lot result message
            lotToUpdate.resultMessage = resultMsg

            this.finalizeLot(model, payload, lotToUpdate, LotActivityStatus.Sold, isCurrent)
        }
    }

    setupPassedInLot(model: AuctionModel, payload: LotStatusUpdatePayload) {
        let isCurrent = AuctionHelper.isLotCurrentById(model, payload.LotId)
        let lotToUpdate: LotModel | null | undefined = isCurrent
            ? model.currentLot
            : AuctionHelper.getNextLot(model, payload.LotId)
        if (lotToUpdate) {
            // Setup lot result message
            lotToUpdate.resultMessage = LotService.message.result.passedIn
            if (LotHelper.isTallyDefinedOnPayload(payload)) {
                lotToUpdate.tally = Number(payload.Tally || 0)
            }
            this.finalizeLot(model, payload, lotToUpdate, LotActivityStatus.PassedIn, isCurrent)
        }
    }

    setupWithdrawnLot(model: AuctionModel, payload: LotStatusUpdatePayload) {
        // Find withdrawn lot
        const withdrawnLot = model.nextLots.find((i) => i.id === payload.LotId)
        // Add withdrawn lot to finished
        model.finishedLots.push(withdrawnLot!)
        // Remove withdrawn lot from upcoming
        model.nextLots = model.nextLots.filter((i) => i.id !== withdrawnLot!.id)
        // Scroll finished lots to focus on recently added
        // this.scrollFinishedLots();
    }

    setupFinishingLot(
        model: AuctionModel,
        status: LotActivityStatus,
        lot: LotModel | null = null,
        resetLastBidder: boolean = true
    ) {
        // this.scrollFinishedLots();

        // Reset timer
        model.timer = 0
        // Do not allow new bids
        model.canBid = false
        // reset last bidder property
        if (resetLastBidder) {
            this.lastBidder = null
        }

        // Setup current lot
        this.setupCurrentLot(model, status, LotService.message.status.finishing, 0, lot)
    }

    /**
     * Lot media update parent
     * @param childLot
     * @param auctionState
     */
    lotUpdateParentMedia(childLot: LotModel, auctionState: AuctionModel) {
        if (LotHelper.hasParent(childLot) && auctionState) {
            let parent = LotHelper.getParent(childLot, auctionState.nextLots)
            if (parent && LotHelper.isPick(parent)) {
                parent.childMedia = Array.isArray(parent.childMedia) ? parent.childMedia : []
                let newMedia = parent.childMedia.filter((l) => l && l.lotNumber !== childLot.lotNumber)

                if (childLot.media && childLot.media.length) {
                    newMedia.push({
                        lotNumber: childLot.lotNumber,
                        media: childLot.media
                    })
                }
                parent.childMedia = newMedia
                this.setupMedia(parent)
            }
        }
    }

    setupMedia(lot: LotModel | null | undefined) {
        if (lot) {
            let newMedia = <any>[]
            if (LotHelper.isPick(lot) && Array.isArray(lot.childMedia)) {
                // get media from children
                lot.childMedia
                    .filter((l) => l && l.lotNumber && Array.isArray(l.media))
                    .forEach((childMedia) => {
                        //merge lot media to array and add lot number to it
                        newMedia = newMedia.concat(
                            childMedia.media.map((x) => {
                                return {
                                    ...x,
                                    ...{
                                        lotNumber: childMedia.lotNumber
                                    }
                                }
                            })
                        )
                    })
            } else {
                newMedia = lot.media && lot.media.length ? lot.media : []
            }

            lot.media =
                newMedia &&
                newMedia.map((item) => ({
                    ...item,
                    isPhoto: item.mediaType === MediaType.Image && item.metaType === MetaType.None,
                    isVideo: item.mediaType === MediaType.Video
                }))
        }
    }

    updateLotDetails(p, model: AuctionModel) {
        // --- [Signal] Update lot details
        const payload = <LotInformationPayload>p
        payload.Lots.forEach((lot) => {
            if (lot.ActivityStatus === LotActivityStatus.Removed) {
                model.nextLots = model.nextLots.filter((x) => x.id !== lot.LotId)
            } else {
                const lotToUpdate = model.nextLots.find((i) => i.id === lot.LotId)
                if (lotToUpdate) {
                    lotToUpdate.increment = lot.Increment
                    lotToUpdate.startPrice = lot.StartPrice
                    lotToUpdate.tally = lot.Tally
                    lotToUpdate.aveWeight = lot.AveWeight
                    lotToUpdate.noReserve = lot.NoReserve
                    lotToUpdate.activityStatus = lot.ActivityStatus
                    if (lotToUpdate.activityStatus === LotActivityStatus.Removed) {
                        //Handle the clearing of child ids and parent ids
                        lotToUpdate.childIds = []
                        this.updateChildrenForCurrentLot(lotToUpdate, model)
                    }
                }
            }
        })
        // ++++++ Lots have been updated
    }

    /**
     * Lot created handler
     * @param auctionState
     * @param payload
     */
    lotCreated(auctionState: AuctionModel, payload: SaleyardLotCreatedPayload) {
        const lot: LotModel = {
            id: payload.Id,
            title: payload.Title,
            lotNumber: payload.LotNumber,
            increment: payload.Increment,
            reservePrice: payload.ReservePrice,
            startPrice: payload.StartPrice || 0,
            activityStatus: payload.ActivityStatus,
            tally: payload.Tally,
            aveWeight: payload.AveWeight,
            status: payload.Status,
            incrementUnits: PricingOptions[payload.IncrementUnits],
            incrementDescription: payload.IncrementDescription,
            noReserve: payload.NoReserve,
            order: payload.Order,
            stockType: payload.StockType,
            agency: payload.Agency,
            agent: payload.Agent,
            listingId: payload.ListingId,
            pricePerHeadEquivalent: payload.PricePerHeadEquivalent,
            pricePerKg: payload.PricePerKg,
            age: payload.Age,
            breed: payload.Breed,
            tbStatus: payload.TbStatus,
            withholding: payload.Withholding,
            vendorName: payload.VendorName,
            description: payload.Description,
            parentId: payload.ParentId,
            childIds: payload.ChildIds,
            lotType: payload.LotType,
            pickNotes: payload.PickNotes,
            runoutMinimumTally: Number(payload.RunoutMinimumTally || 0)
        }

        // update on lot start for picks children may changed
        if (LotHelper.isPick(lot)) {
            if (payload.ChildMedia) {
                let newChildMedia = objectKeysFirstLetterToLowerCase(payload.ChildMedia)
                if (Array.isArray(newChildMedia)) {
                    lot.childMedia = newChildMedia
                    this.setupMedia(lot)
                }
            }
        }

        auctionState.nextLots.push(lot)
        this.updateChildrenForCurrentLot(lot, auctionState)
        this.sortLots(auctionState.nextLots)
    }

    /**
     * Lot updated handler
     * @param auctionState
     * @param payload
     */
    lotUpdated(auctionState: AuctionModel, payload: SaleyardLotUpdatedPayload) {
        const lot =
            auctionState.nextLots.find((l) => l.id === payload.Id) ||
            (auctionState.currentLot!.id === payload.Id ? auctionState.currentLot : null)
        if (lot) {
            lot.title = payload.Title
            lot.lotNumber = payload.LotNumber
            lot.increment = payload.Increment
            lot.reservePrice = payload.ReservePrice
            lot.startPrice = payload.StartPrice || 0
            lot.activityStatus = payload.ActivityStatus

            if (
                (payload.ActivityStatus === LotActivityStatus.Started ||
                    payload.ActivityStatus === LotActivityStatus.NowSelling) &&
                (payload.Tally !== auctionState.currentLot!.tally ||
                    payload.AveWeight !== auctionState.currentLot!.aveWeight)
            ) {
                const sound = this.messageService.getMessage('auction.sounds.lotDetailChange')
                this.soundService.playSound(sound)
            }

            lot.tally = payload.Tally
            lot.aveWeight = payload.AveWeight

            lot.status = payload.Status
            lot.incrementUnits = PricingOptions[payload.IncrementUnits]
            lot.incrementDescription = payload.IncrementDescription
            lot.noReserve = payload.NoReserve
            lot.order = payload.Order
            lot.stockType = payload.StockType
            lot.agency = payload.Agency
            lot.agent = payload.Agent
            lot.listingId = payload.ListingId
            lot.pricePerHeadEquivalent = payload.PricePerHeadEquivalent
            lot.pricePerKg = payload.PricePerKg
            lot.age = payload.Age
            lot.breed = payload.Breed
            lot.tbStatus = payload.TbStatus
            lot.withholding = payload.Withholding
            lot.vendorName = payload.VendorName
            lot.description = payload.Description
            lot.childIds = payload.ChildIds
            lot.lotType = payload.LotType
            lot.pickNotes = payload.PickNotes
            lot.runoutMinimumTally = Number(payload.RunoutMinimumTally || 0)
        }

        // update on lot start for picks children may changed
        if (lot && LotHelper.isPick(lot)) {
            if (payload.ChildMedia) {
                let newChildMedia = objectKeysFirstLetterToLowerCase(payload.ChildMedia)
                if (Array.isArray(newChildMedia)) {
                    lot.childMedia = newChildMedia
                    this.setupMedia(lot)
                }
            }
        }

        this.updateChildrenForCurrentLot(lot!, auctionState)
        this.sortLots(auctionState.nextLots)
    }

    /**
     * ADO65701: Includes currentLot and cancelledLot:
     * after Confirm → Cancel the last lot stays outside nextLots (stay-on-last-selected),
     * so LotCreated must still link it.
     */
    private getLotsForChildLinking(state: AuctionModel): LotModel[] {
        const lots = [...state.nextLots]
        for (const lot of [state.currentLot, state.cancelledLot]) {
            if (lot?.id != null && !lots.some((existing) => existing.id === lot.id)) {
                lots.push(lot)
            }
        }
        return lots
    }

    private updateChildrenForCurrentLot(parentLot: LotModel, state: AuctionModel) {
        const candidates = this.getLotsForChildLinking(state)
        const lotHasChildren = parentLot.childIds && parentLot.childIds.length
        if (lotHasChildren) {
            //remove old children
            const oldChildren = candidates.filter(
                (e) => parentLot.childIds!.indexOf(Number(e.id)) == -1 && e.parentId === parentLot.id
            )
            oldChildren.forEach((childLot) => this.setParentIdAndLotType(childLot, LotType.Default, null))

            //update current children
            const childLots = candidates.filter((e) => parentLot.childIds!.indexOf(Number(e.id)) != -1)
            childLots.forEach((childLot) => this.setParentIdAndLotType(childLot, parentLot.lotType!, parentLot.id!))
        } else {
            //remove any children which had previously been related
            const oldChildren = candidates.filter((e) => e.parentId === parentLot.id)
            oldChildren.forEach((childLot) => this.setParentIdAndLotType(childLot, LotType.Default, null))
        }
    }

    private setParentIdAndLotType(childLot: LotModel, lotType: LotType, parentId: number | null) {
        childLot.lotType = lotType
        childLot.parentId = parentId
    }

    /**
     * Lot order updated handler
     * @param auctionState
     * @param payload
     */
    lotOrderUpdated(auctionState: AuctionModel, payload: LotOrderUpdatePayload) {
        for (let key in payload.LotOrderDictionary) {
            let value = payload.LotOrderDictionary[key]
            const lot = auctionState.nextLots.find((l) => l.id === Number(key))
            if (lot) {
                lot.order = value
            }
        }

        this.sortLots(auctionState.nextLots)
    }

    /**
     * Lot sale price updated handler
     * @param auctionState
     * @param payload
     */
    updateFinishedLotPricingChange(auctionState: AuctionModel, payload: LotSalePriceChangePayload) {
        const lot = auctionState.finishedLots.find((l) => l.id === payload.LotId)
        if (lot) {
            lot.soldPrice = payload.Amount
        }
    }

    /**
     * Lot post auction sale
     * @param auctionState
     * @param payload
     */
    updateFinishedLotPostAuctionSale(auctionState: AuctionModel, payload: LotPostAuctionSaleCreatedPayload) {
        const lot = auctionState.finishedLots.find((l) => l.id === payload.LotId)
        if (lot) {
            lot.soldPrice = payload.SoldPrice
            lot.bidder = payload.Bidder
            lot.purchaserAccount = payload.PurchaserAccount
            lot.purchaserName = payload.PurchaserName
            lot.activityStatus = payload.ActivityStatus
        }
    }

    /**
     * Lot offer update
     * @param auctionState
     * @param payload
     */
    updateOffer(auctionState: AuctionModel, offerUpdate: OfferUpdatePayload) {
        if (auctionState.currentLot != null && auctionState.currentLot.id === offerUpdate.LotId) {
            switch (offerUpdate.Status) {
                case LotOfferStatus.Offered:
                    // creation of offer
                    if (!auctionState.currentLot.offers) {
                        auctionState.currentLot.offers = []
                    }
                    auctionState.currentLot.offers.push({
                        id: offerUpdate.OfferId,
                        lotId: offerUpdate.LotId,
                        amount: offerUpdate.Amount,
                        biddingRun: offerUpdate.BiddingRun,
                        status: offerUpdate.Status,
                        auctionBidderId: offerUpdate.BidderId,
                        acceptedLotIds: offerUpdate.AcceptedLotIds,
                        acceptedTally: offerUpdate.AcceptedTally,
                        availableTally: offerUpdate.AvailableTally
                    })
                    if (
                        auctionState.auction!.bidderAccount &&
                        auctionState.auction!.bidderAccount.id === offerUpdate.BidderId
                    ) {
                        //scroll pick child lots selection panel
                        setTimeout(() => {
                            const yOffset = -25
                            let elToScroll = <any>null
                            let elIds = ['lot-bidding-card', 'bidding-section-wrap']
                            let elId = elIds.find((elId) => {
                                let element = document.getElementById(elId)
                                return element && element.classList && element.classList.contains('scroll-to')
                            })
                            elToScroll = elId ? document.getElementById(elId) : null
                            if (elToScroll) {
                                const y = elToScroll.getBoundingClientRect().top + window.pageYOffset + yOffset
                                window.scrollTo({ top: y, behavior: 'smooth' })
                            }
                        }, 500)

                        //play sound ???
                    }
                    break
                default:
                    // update offer
                    if (auctionState.currentLot.offers) {
                        let offer = auctionState.currentLot.offers.find((e) => e.id == offerUpdate.OfferId)
                        if (offer) {
                            offer.status = offerUpdate.Status
                            offer.acceptedLotIds = offerUpdate.AcceptedLotIds
                            offer.acceptedTally = offerUpdate.AcceptedTally
                            offer.availableTally = offerUpdate.AvailableTally
                        }
                    }
                    break
            }
        }
    }

    /** lot Media Added
     * @param auctionState
     * @param payload
     */
    lotMediaAdded(auctionState: AuctionModel, payload: SaleyardLotMediaAddedPayload) {
        const lot =
            auctionState.nextLots.find((l) => l.id === payload.LotId) ||
            (auctionState.currentLot!.id === payload.LotId ? auctionState.currentLot : null)
        if (lot && payload.MediaMeta) {
            if (!Array.isArray(lot.media)) {
                lot.media = []
            }
            lot.media.push(LotHelper.mapUpdateMediaPayload(payload.MediaMeta))
            this.lotUpdateParentMedia(lot, auctionState)
        }
    }

    /**
     * Lot media removed
     * @param auctionState
     * @param payload
     */
    lotMediaRemoved(auctionState: AuctionModel, payload: SaleyardLotMediaRemovedPayload) {
        //find in next lots by media ID
        auctionState.nextLots.forEach((lot, lotIndex) => {
            if (Array.isArray(lot.media)) {
                lot.media.forEach((m, mIndex) => {
                    if (m.id === payload.MediaId) {
                        delete auctionState.nextLots[lotIndex].media![mIndex]
                        this.lotUpdateParentMedia(auctionState.nextLots[lotIndex], auctionState)
                    }
                })
            }
        })

        //find in currentLot by media ID
        if (Array.isArray(auctionState.currentLot!.media)) {
            auctionState.currentLot!.media.forEach((m, mIndex) => {
                if (m.id === payload.MediaId) {
                    delete auctionState.currentLot!.media![mIndex]
                }
            })
        }
    }

    validateBidAmount(currentPrice, increment: number, bid: number) {
        // Setup default validation result
        const result = { status: true, msg: '' }
        // Check whether bid is specified
        if (bid == null) {
            result.status = false
            result.msg = ''
            return result
        }

        // Make sure that bid is a string
        const bidString = bid.toString()
        // Check whether input is empty
        if (bidString === '') {
            result.status = false
            result.msg = ''
            return result
        }

        // Check whether bid is not a number
        if (isNaN(bid)) {
            result.status = false
            result.msg = LotService.message.validation.invalid
            return result
        }

        // Get index of delimiter point
        const inxedOfDp = bidString.indexOf('.')
        if (inxedOfDp !== -1 && bidString.length - inxedOfDp > 3) {
            result.status = false
            result.msg = LotService.message.validation.twoDpAllowed
            return result
        }

        // Parse bid
        const bidAmount: any = parseFloat(bidString).toFixed(2)
        // Check whether bid amount is less than current price
        if (bidAmount < currentPrice + increment) {
            result.status = false
            result.msg = LotService.message.validation.lowNumber
            return result
        }

        return result
    }

    setupAutoBid(model: AuctionModel, p) {
        const payload = <LotAutoBidConfigurationPayload>p
        // --- [Signal] Set auto bid
        // Set auto bid
        model.currentLot!.autoBid = payload.Amount
        // Setup auto bid state
        model.currentLot!.autoBidIsSet = true
        // Hide spinner
        this.spinner.hide()
        // ++++++ Auto bid is set
    }

    setupFinishedLots(model: AuctionModel, lots) {
        const result = lots.map((lot) => {
            // Check whether bidder won current lot
            if (model.auction!.bidderAccount != null && lot.bidderId === model.auction!.bidderAccount.id) {
                // Setup winning bidder
                lot.userWonBidding = true
            }

            return lot
        })

        return result
    }

    private scrollFinishedLots() {
        // Scroll table to show next lot in view
        setTimeout(() => {
            const row = document.getElementById('object-currentLot')
            if (row) {
                const topPosition = row!.offsetTop
                document.getElementById('auction-lots-table')!.scrollTop = topPosition
            }
        }, 1000)
    }

    getSound(auction: AuctionResponse, lot: LotModel, payload: LotStatusUpdatePayload): string {
        // increment change
        const incrementChange = payload.Increment !== lot.increment
        // bid placed
        const priceChanged = lot.currentPrice !== payload.CurrentPrice
        const bidPlaced = priceChanged && payload.WinningBidderId
        // leading bid
        const outBid =
            bidPlaced &&
            auction.bidderAccount !== undefined &&
            this.lastBidder === auction.bidderAccount!.id &&
            this.lastBidder !== payload.WinningBidderId

        // out bid
        const leadingBid = bidPlaced && !outBid
        const bidPlacedAtCallPrice = !priceChanged && payload.AllBidderIds.length && !this.lastBidder
        // initial price change
        const initialPriceChanged = priceChanged && !payload.WinningBidderId
        // Near reserve
        const nearReserve =
            payload.ReserveStatus !== lot.reserveStatus && payload.ReserveStatus === ReserveStatus.NearReserve
        // tally change
        const tallyChange = payload.Tally !== lot.tally
        // average weight change
        const avgWeightChange = payload.AveWeight !== lot.aveWeight
        // lot is finishing
        let sound: string = ''
        if (initialPriceChanged || tallyChange || avgWeightChange || nearReserve) {
            sound = 'auction.sounds.lotDetailChange'
        } else if (incrementChange && !(leadingBid || outBid || bidPlacedAtCallPrice)) {
            sound = 'auction.sounds.lotDetailChange'
        } else if (outBid) {
            sound = 'auction.sounds.bidOutbid'
        } else if (leadingBid || bidPlacedAtCallPrice) {
            sound = 'auction.sounds.bidPlaced'
        }

        return sound ? this.messageService.getMessage(sound) : ''
    }

    updateCurrentLot(model: AuctionModel, payload: LotStatusUpdatePayload) {
        const sound = this.getSound(model.auction!, model.currentLot!, payload)

        // set last bidder property to whoever has winning bid
        this.lastBidder = typeof payload.WinningBidderId !== 'undefined' ? payload.WinningBidderId : null

        if (payload.TimeExtended) {
            model.currentLot!.extensionsCount! += 1
        }

        model.currentLot!.reserveStatus = payload.ReserveStatus
        // Set current price
        model.currentLot!.currentPrice = payload.CurrentPrice
        // Set increment
        model.currentLot!.increment = payload.Increment
        // Set total price
        model.currentLot!.totalPrice = payload.TotalPrice
        // Set price per unit
        model.currentLot!.pricePerKg = payload.PricePerUnit
        // Set price per head equivalent
        model.currentLot!.pricePerHeadEquivalent = payload.PricePerHeadEquivalent
        // Set winning bidder id
        model.currentLot!.winningBidderId = payload.WinningBidderId
        // Set next bid value
        model.currentLot!.bid = payload.CurrentPrice! + payload.Increment!
        // Set bidders ids
        model.currentLot!.biddersIds = payload.AllBidderIds
        // Set lot closing
        model.currentLot!.lotClosing = payload.LotClosing
        // Set lot 'Now Selling'
        model.currentLot!.nowSelling = payload.NowSelling

        // Update tally
        model.currentLot!.tally = payload.Tally
        // Update weight
        model.currentLot!.aveWeight = payload.AveWeight

        const lotIsFinishedDelayed = payload.Status === LotStatus.FinishDelayed
        if (model.currentLot && lotIsFinishedDelayed) {
            model.currentLot.activityStatus = LotActivityStatus.FinishDelayed
        }

        if (sound && !lotIsFinishedDelayed) {
            this.soundService.playSound(sound)
        }
    }

    updatePickChildLot(model: AuctionModel, payload: LotStatusUpdatePayload) {
        // --- updatePickChildLot
        //this is reusing update current lot
        // not sure if all needed to be updated for pick child
        let lotToUpdateIndex = model.nextLots.findIndex((x) => x.id === payload.LotId)
        if (lotToUpdateIndex !== -1 && model.nextLots[lotToUpdateIndex]) {
            // const sound = this.getSound(model.auction, model.nextLots[lotToUpdateIndex], payload);

            // // set last bidder property to whoever has winning bid
            // this.lastBidder = payload.WinningBidderId;

            model.nextLots[lotToUpdateIndex].reserveStatus = payload.ReserveStatus
            // Set current price
            model.nextLots[lotToUpdateIndex].currentPrice = payload.CurrentPrice
            // Set increment
            model.nextLots[lotToUpdateIndex].increment = payload.Increment
            // Set total price
            model.nextLots[lotToUpdateIndex].totalPrice = payload.TotalPrice
            // Set price per unit
            model.nextLots[lotToUpdateIndex].pricePerKg = payload.PricePerUnit
            // Set price per head equivalent
            model.nextLots[lotToUpdateIndex].pricePerHeadEquivalent = payload.PricePerHeadEquivalent
            // Set winning bidder id
            model.nextLots[lotToUpdateIndex].winningBidderId = payload.WinningBidderId
            // Set next bid value
            model.nextLots[lotToUpdateIndex].bid = payload.CurrentPrice! + payload.Increment!
            // Set bidders ids
            model.nextLots[lotToUpdateIndex].biddersIds = payload.AllBidderIds
            // Set lot closing
            model.nextLots[lotToUpdateIndex].lotClosing = payload.LotClosing
            // Set lot 'Now Selling'
            model.nextLots[lotToUpdateIndex].nowSelling = payload.NowSelling

            // Update PurchaserName if present
            if (payload.PurchaserName && payload.PurchaserName !== '') {
                model.nextLots[lotToUpdateIndex].purchaserName = payload.PurchaserName
            }

            // Update PurchaserAccount if present
            if (payload.PurchaserAccount && payload.PurchaserAccount !== '') {
                model.nextLots[lotToUpdateIndex].purchaserAccount = payload.PurchaserAccount
            }

            // Update tally
            model.nextLots[lotToUpdateIndex].tally = payload.Tally
            // Update weight
            model.nextLots[lotToUpdateIndex].aveWeight = payload.AveWeight
            //update state
            if (LotHelper.mapLotStatusToActivityStatus(payload.Status)) {
                model.nextLots[lotToUpdateIndex].activityStatus = LotHelper.mapLotStatusToActivityStatus(payload.Status)
            }

            //FINISH LOT
            if (payload.WinningBidderId) {
                model.nextLots[lotToUpdateIndex].winningBidderId = payload.WinningBidderId
                //if sold
                if (LotHelper.isLotSoldOrPostAuctionSold(model.nextLots[lotToUpdateIndex])) {
                    model.nextLots[lotToUpdateIndex].soldPrice = payload.CurrentPrice
                    // Setup winning lot state
                    if (
                        model.auction!.bidderAccount != null &&
                        payload.WinningBidderId === model.auction!.bidderAccount.id
                    ) {
                        // let resultMsg = LotService.message.result.sold
                        // Get sold price in currency format
                        // const soldPrice = this.currency.transform(model.nextLots[lotToUpdateIndex].soldPrice)
                        // Update result message
                        // resultMsg = LotService.message.result.soldToUser(soldPrice, model.nextLots[lotToUpdateIndex].incrementUnits)
                        // Set user won indicator
                        model.nextLots[lotToUpdateIndex].userWonBidding = true
                    }

                    if (model.nextLots[lotToUpdateIndex].userWonBidding) {
                        const sound = this.messageService.getMessage('auction.sounds.bidWin')
                        this.soundService.playSound(sound)
                    }
                }
                //move lot to finished lots
                model.finishedLots.push(model.nextLots[lotToUpdateIndex])
                model.nextLots.splice(lotToUpdateIndex, 1)
            }

            //not finish delay and no sound per pick child
            // const lotIsFinishedDelayed = payload.Status === LotStatus.FinishDelayed;
            // if(model.currentLot && lotIsFinishedDelayed) {
            //   model.currentLot.activityStatus = LotActivityStatus.FinishDelayed;
            // }

            // if (sound && !lotIsFinishedDelayed) { this.soundService.playSound(sound); }
        }
    }

    private sortLots(lots: LotModel[]) {
        lots.sort((a, b) => (a.order! < b.order! ? -1 : a.order! > b.order! ? 1 : 0))
    }

    private setupCurrentLot(
        model: AuctionModel,
        lotStatus: LotActivityStatus,
        lotMsg: string,
        time,
        lot: LotModel | null = null,
        signal: HubClientPacket | null = null
    ) {
        switch (lotStatus) {
            case LotActivityStatus.Sold:
            case LotActivityStatus.PassedIn:
            case LotActivityStatus.NotSold:
                // add current lot to prop to save state for next status for table
                this.currentLot = model.currentLot
                break
            case LotActivityStatus.Started:
                // add previous current lot to finished lots for table
                if (this.currentLot) {
                    model.finishedLots.push(this.currentLot)
                    this.currentLot = undefined
                }
                break
        }

        // Check whether lot is provided
        if (lot != null) {
            // Setup current lot
            model.currentLot = lot
        }

        /**
         * for formatting the table with withdrawn lots: check if there are next lots, check if the order is less than current lot order no,
         * and then check that the next lot is either of the withdrawn statuses. if all true, then move it to the finished
         * lots array and resort that based on order no.
         */
        if (
            model.nextLots.length &&
            model.currentLot!.order! > model.nextLots[0]!.order! &&
            (model.nextLots[0].activityStatus === LotActivityStatus.Withdrawn ||
                model.nextLots[0].activityStatus === LotActivityStatus.WithdrawnNotSold)
        ) {
            model.finishedLots.push(model.nextLots[0])
            model.nextLots.shift()
            model.finishedLots.sort((a, b) => a.order! - b.order!)
        }

        // Check whether current price is not set
        if (model.currentLot!.currentPrice == null) {
            // Setup current price
            model.currentLot!.currentPrice = model.currentLot!.startPrice
        }

        // Calculate bid
        const bid = model.currentLot!.currentPrice! + model.currentLot!.increment!
        // Setup bid
        model.currentLot!.bid = bid
        // Set lot status
        model.currentLot!.activityStatus = lotStatus
        // Set lot extensions count
        model.currentLot!.extensionsCount = 0
        // Setup lot status message
        model.currentLot!.statusMessage = lotMsg
        // Setup winning bid message. Bidder id is being returned from API or set by bid signal handler
        let isUsersBid =
            model.auction!.bidderAccount != null &&
            model.currentLot!.winningBidderId === model.auction!.bidderAccount.id
        // Check whether signal details provided
        if (signal != null) {
            // Get payload
            const payload = <LotCommencementPayload>signal.Payload
            // Setup winning bid from signal
            isUsersBid =
                model.auction!.bidderAccount != null && payload.WinningBidderId === model.auction!.bidderAccount.id
            // Set lot reserve status
            model.currentLot!.reserveStatus = payload.ReserveStatus
            model.currentLot!.biddingRun = payload.BiddingRun
        }

        // Set winning bid message
        model.currentLot!.isUsersBid = isUsersBid
        // Setup timer
        this.setupTimer(model, time)
    }

    private setupTimer(model, delay) {
        // Stop timer
        clearInterval(this.timer)

        // Setup last time timer updated
        this.timerUpdatedAt = Date.now()

        // Set timeout
        model.timer = delay
        const step = 500
        // Run timer
        this.timer = setInterval(() => {
            if (model.timer <= 0) {
                clearInterval(this.timer)
                return
            }
            // Setup current datestamp
            const currentDate = Date.now()

            // Calculate how much time left since last update
            model.timer = model.timer - (currentDate - this.timerUpdatedAt)

            // Update datestamp when timer has been updated
            this.timerUpdatedAt = currentDate
        }, step)
    }

    setupLotUnderOffer(model: AuctionModel, payload: LotStatusUpdatePayload) {
        model.currentLot!.activityStatus = LotHelper.mapLotStatusToActivityStatus(payload.Status)
        //runout update tally
        if (LotHelper.isTallyDefinedOnPayload(payload)) {
            model.currentLot!.tally = Number(payload.Tally || 0)
        }
    }

    finalizeLot(
        model: AuctionModel,
        payload: LotStatusUpdatePayload,
        lotToUpdate: LotModel,
        newState: LotActivityStatus,
        isCurrent: boolean | null | undefined = true
    ) {
        // Setup finishing state
        if (isCurrent) {
            model.currentLot = lotToUpdate
            this.setupFinishingLot(model, newState, null)
        } else if (lotToUpdate) {
            // newly created runout child that is never current lot needs to be mark as sold
            //mark as sold
            lotToUpdate.activityStatus = newState
            //move to finished lots
            let nextLotIndex = model.nextLots.findIndex((x) => x.id === lotToUpdate.id)
            let finishedLotIndex = model.finishedLots.findIndex((x) => x.id === lotToUpdate.id)
            if (nextLotIndex !== -1) {
                model.nextLots.splice(nextLotIndex, 1)
            }
            if (finishedLotIndex === -1) {
                model.finishedLots.push(lotToUpdate)
            }
        }
    }
}
